import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { CountBadge, Tooltip, cn } from '@micarahr/web-ui';
import { roleLabel } from '@micarahr/permissions/web';
import { NAVIGATION } from '../../app/navigation';
import { usePermission, useUiStore } from '../../stores';
import { env } from '../../config/env';
import { Avatar } from '@micarahr/web-ui';
import { BrandMark } from '../brand-mark';

/**
 * The sidebar.
 *
 * Light, not black (§7). Grouped, permission-filtered, and collapsible to icons
 * for a 1280px laptop where 248px of chrome is a real cost. A group whose every
 * item is hidden by permission disappears with its heading — an empty "Payroll
 * and Compensation" label tells a recruiter about a module they cannot open.
 */

export function Sidebar({ badges }: { badges: Record<string, number> }) {
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggle = useUiStore((state) => state.toggleSidebar);
  const { canAny, session } = usePermission();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const groups = NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => canAny(item.permissions)),
  })).filter((group) => group.items.length > 0);

  /*
   * One item is active: the longest destination the current path sits under.
   *
   * A plain `startsWith` lights the parent as well as the child, because
   * `/attendance/review` does start with `/attendance` — and the same
   * held for leave and payroll, each of which has children. Two
   * highlighted rows say the app is in two places at once.
   *
   * Longest-match rather than exact-match, so a detail route still highlights
   * its module: `/employees/8f2c` has no nav entry of its own and belongs
   * under Employees.
   */
  const activePath = groups
    .flatMap((group) => group.items.map((item) => item.to))
    .filter((to) =>
      to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`),
    )
    .sort((a, b) => b.length - a.length)[0];

  return (
    <aside
      className={cn(
        'group/sidebar relative flex h-full shrink-0 flex-col border-r border-line bg-surface-sidebar transition-[width] duration-150',
        collapsed ? 'w-[64px]' : 'w-[248px]',
      )}
      aria-label="Main navigation"
    >
      {/* Brand ------------------------------------------------------------- */}
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-line',
          collapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        {collapsed ? (
          <BrandMark className="h-8 w-8 shrink-0" />
        ) : (
          <div className="flex min-w-0 items-center gap-2.5">
            <BrandMark className="h-8 w-8 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-baseline gap-1">
                <span className="text-md font-extrabold tracking-tight text-ink">Micara</span>
                <span className="text-md font-extrabold tracking-tight text-brand">HR</span>
              </div>
              <p className="truncate text-micro text-ink-muted">Micara Technologies Corp.</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation -------------------------------------------------------- */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {groups.map((group) => (
          <div key={group.label} className="mb-4 last:mb-0">
            {!collapsed ? (
              <h2 className="mb-1 px-2 text-micro font-semibold uppercase tracking-wide text-ink-subtle">
                {group.label}
              </h2>
            ) : (
              <div className="mx-auto mb-2 h-px w-6 bg-line" aria-hidden />
            )}
            <ul className="space-y-px">
              {group.items.map((item) => {
                const active = item.to === activePath;
                const badge = item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0;
                const Icon = item.icon;

                return (
                  <li key={item.to}>
                    <Tooltip content={item.label} side="right" hidden={!collapsed}>
                      <Link
                        to={item.to}
                        /*
                         * The router's own active matching is prefix-based and
                         * sets `aria-current` itself, so `/attendance` was
                         * announced as the current page while sitting on
                         * `/attendance/review` — the class said one thing
                         * and the accessibility tree said another.
                         *
                         * `exact` stops the router marking parents. Where a
                         * detail route has no nav entry of its own, the
                         * longest-match calculation above still highlights the
                         * module, and the attribute below follows it.
                         */
                        activeOptions={{ exact: true }}
                        aria-current={active ? 'page' : undefined}
                        // Collapsed, the label is not rendered and the tooltip
                        // is not an accessible name — without this the link
                        // announces as "link" and nothing else.
                        aria-label={item.label}
                        className={cn(
                          'group relative flex items-center gap-2.5 rounded-md text-base transition-colors',
                          collapsed ? 'justify-center px-0 py-2' : 'px-2 py-1.5',
                          active
                            ? 'bg-brand-soft font-medium text-brand-hover'
                            : 'text-ink-secondary hover:bg-neutral-soft hover:text-ink',
                        )}
                      >
                        {/* The thin blue indicator the spec asks for (§8). */}
                        {active ? (
                          <span
                            className="absolute -left-2 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-brand"
                            aria-hidden
                          />
                        ) : null}
                        <Icon
                          className={cn(
                            'h-4 w-4 shrink-0',
                            active ? 'text-brand' : 'text-ink-muted',
                          )}
                          aria-hidden
                        />
                        {!collapsed ? (
                          <>
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                            {badge > 0 ? (
                              <CountBadge count={badge} tone={active ? 'brand' : 'neutral'} />
                            ) : null}
                          </>
                        ) : null}
                      </Link>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer ------------------------------------------------------------ */}
      <div className="shrink-0 border-t border-line p-2">
        {/* Only mock mode shows fictional records; against the real API the
            claim itself would be the fiction. */}
        {env().dataSource === 'mock' ? (
          <div
            className={cn(
              'mb-2 rounded-md border border-warning-border bg-warning-soft px-2 py-1.5',
              collapsed && 'px-1 text-center',
            )}
          >
            <p className="text-micro font-semibold uppercase tracking-wide text-warning">
              {collapsed ? 'DEMO' : 'Demonstration data'}
            </p>
            {!collapsed ? (
              <p className="mt-0.5 text-micro leading-tight text-ink-secondary">
                Fictional records. Not connected to payroll or government systems.
              </p>
            ) : null}
          </div>
        ) : null}

        {session ? (
          <div
            className={cn(
              'flex items-center gap-2 rounded-md px-1 py-1',
              collapsed && 'justify-center',
            )}
          >
            <Avatar name={session.displayName} size="sm" />
            {!collapsed ? (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{session.displayName}</p>
                <p className="truncate text-micro text-ink-muted">
                  {roleLabel(session.primaryRole)}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/*
       * The collapse control, on the edge rather than in the footer.
       *
       * It was a full-width row under the profile, which put a piece of
       * window chrome in the same visual group as the person's name and the
       * demonstration-data warning — three unrelated things reading as a
       * list. On the border it belongs to the boundary it moves, which is
       * also where the cursor already is when somebody reaches for it.
       *
       * Half outside the sidebar, so it stays reachable when collapsed to
       * 64px, and `-translate-y-1/2` centres it on the seam. It sits above
       * the content with `z-20` because the page beside it scrolls under.
       */}
      <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className={cn(
            'absolute -right-3 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center',
            'rounded-full border border-line bg-surface text-ink-muted shadow-sm',
            'transition-all hover:border-brand hover:text-brand',
            /*
             * Always present, quietly.
             *
             * Hiding it until the sidebar is hovered is tidier and costs more
             * than it saves: a 24px circle nobody can find is worse than the
             * full-width row it replaced. It sits at 60% until the cursor is
             * nearby, which keeps it out of the way without making somebody
             * hunt for it.
             */
            'opacity-60 focus-visible:opacity-100 group-hover/sidebar:opacity-100',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
          )}
        >
          <ChevronLeft
            className={cn('h-3.5 w-3.5 transition-transform', collapsed && 'rotate-180')}
          />
        </button>
      </Tooltip>
    </aside>
  );
}
